import React, { useState, useEffect } from 'react';
import {
    Dialog,
    Paper,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Typography,
    Box,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Card,
    CardContent,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Order, UpdateOrderParams } from '../orders.types';
import { updateOrder, getOrderStatuses } from '../orders.api';
import FiyrPodLogo from '../../../../../../src/logos/FiyrPod_Logo.png';
import { Organization } from '../../../../../utils';
import { adminViewOrganization } from '../../../admin.organizations.api';
import Fp1PodSerialAssignmentModal from '../../fp1/components/Fp1PodSerialAssignmentModal';

interface OrderDialogProps {
    open: boolean;
    order: Order | null;
    onClose: () => void;
    onSave: () => void;
    /** When true, FP1 Hardware Inventory → Orders subtab: pod serial assignment UI and tracking gate. */
    fp1HardwareOrdersTab?: boolean;
    /** Receives the updated marketplace order after pod assignments are saved (FP1 tab only). */
    onFp1OrderRefresh?: (order: Order) => void;
}

// Styled components for print support
const PrintableDialog = styled(Dialog)(() => ({
    '@media print': {
        '& .MuiDialog-container': {
            position: 'static !important',
        },
        '& .MuiDialog-paper': {
            boxShadow: 'none !important',
            maxWidth: '100% !important',
            margin: '0 !important',
            padding: '10px !important',
        },
        '& .no-print': {
            display: 'none !important',
        },
        '& .print-only': {
            display: 'block !important',
        },
    },
}));

const PageBreakAvoid = styled(Box)({
    '@media print': {
        pageBreakAvoid: 'avoid',
    },
});

const SummaryCard = styled(Card)(({ theme }) => ({
    marginBottom: 0,
    height: '100%',
    '& .MuiCardContent-root': {
        padding: theme.spacing(2),
    },
}));

const OrderDialog: React.FC<OrderDialogProps> = ({
    open,
    order,
    onClose,
    onSave,
    fp1HardwareOrdersTab = false,
    onFp1OrderRefresh,
}) => {
    const [orderStatus, setOrderStatus] = useState<string>(order?.order_status || '');
    const [orderNotes, setOrderNotes] = useState<string>('');
    const [trackingNumber, setTrackingNumber] = useState<string>('');
    const [returnTrackingNumber, setReturnTrackingNumber] = useState<string>('');
    const [podModalOpen, setPodModalOpen] = useState(false);
    const [sellerOrg, setSellerOrg] = useState<Organization | null>(null);
    const [buyerOrg, setBuyerOrg] = useState<Organization | null>(null);
    const [orderStatuses, setOrderStatuses] = useState<string[]>([]);
    const [updateOrderModalOpen, setUpdateOrderModalOpen] = useState<boolean>(false);
    const [updateOrderResult, setUpdateOrderResult] = useState<string>('');

    // Return tracking warning (replacement orders only)
    const [returnTrackingWarningOpen, setReturnTrackingWarningOpen] = useState(false);
    const [pendingSaveParams, setPendingSaveParams] = useState<UpdateOrderParams | null>(null);

    useEffect(() => {
        setSellerOrg(null);
        setBuyerOrg(null);

        const fetchSellerOrg = async (sellerOrgUuid: string) => {
            try {
                const { data } = await adminViewOrganization(sellerOrgUuid);
                setSellerOrg(data);
            } catch (e) {
                console.error('Error fetching organization details');
                setSellerOrg(null);
            }
        };

        const fetchBuyerOrg = async (buyerOrgUuid: string) => {
            try {
                const { data } = await adminViewOrganization(buyerOrgUuid);
                setBuyerOrg(data);
            } catch (e) {
                console.error('Error fetching organization details');
                setBuyerOrg(null);
            }
        };

        const fetchOrderStatuses = async () => {
            const response = await getOrderStatuses();
            setOrderStatuses(response.data);
        };

        if (order) {
            setOrderStatus(order.order_status);
            setOrderNotes(order.order_notes || '');
            setTrackingNumber(order.shipping_tracking_number || '');
            setReturnTrackingNumber(order.return_tracking_number || '');
            fetchSellerOrg(order.seller_org_uuid);

            if (order.buyer_org_uuid) {
                fetchBuyerOrg(order.buyer_org_uuid);
            }
        }

        fetchOrderStatuses();
    }, [order]);

    useEffect(() => {
        setPodModalOpen(false);
    }, [order?.uuid]);

    const executeSave = async (params: UpdateOrderParams) => {
        if (!order) return;
        try {
            await updateOrder(order.uuid, params);
            setUpdateOrderResult('Update successful.');
        } catch {
            setUpdateOrderResult('Update failed.');
        } finally {
            onSave();
            setUpdateOrderModalOpen(true);
        }
    };

    const handleSave = async () => {
        if (!order) return;

        const updateParams: UpdateOrderParams = {
            order_status: orderStatus,
            order_notes: orderNotes,
            shipping_tracking_number: trackingNumber,
            return_tracking_number: returnTrackingNumber,
        };

        // Warn if replacement order has outbound tracking but no return tracking number
        const needsReturnTrackingWarning =
            order.order_type === 'replacement' &&
            trackingNumber.trim() !== '' &&
            returnTrackingNumber.trim() === '';

        if (needsReturnTrackingWarning) {
            setPendingSaveParams(updateParams);
            setReturnTrackingWarningOpen(true);
            return;
        }

        await executeSave(updateParams);
    };

    const formatCurrency = (amount: number, isDiscount: boolean = false): string => {
        const absAmount = Math.abs(amount) / 100;
        const formattedAmount = `$${absAmount.toFixed(2)}`;

        if (isDiscount) {
            return `(${formattedAmount})`;
        }

        return formattedAmount;
    };

    const handlePrint = () => {
        window.print();
    };

    const getOrderStatusColor = (status: string) => {
        const statusColors: {
            [key: string]:
                | 'default'
                | 'primary'
                | 'secondary'
                | 'error'
                | 'info'
                | 'success'
                | 'warning';
        } = {
            pending: 'warning',
            processing: 'info',
            shipped: 'primary',
            delivered: 'success',
            cancelled: 'error',
        };
        return statusColors[status] || 'default';
    };

    interface ChecklistItemType {
        text: string;
        requiresInitial?: boolean;
    }

    const getChecklistItems = (): ChecklistItemType[] => {
        if (!order) return [];

        const items: ChecklistItemType[] = [
            { text: 'Ready to Ship', requiresInitial: true },
            { text: 'Flashed - Org, Club ID, Unit #, & Register SN' },
        ];

        return items;
    };

    if (!order) return null;

    const fp1PodRequired = order.fp1_pod_required_count ?? 0;
    const trackingLocked = !!order.shipping_tracking_number_locked;

    return (
        <>
            {fp1HardwareOrdersTab && order && (
                <Fp1PodSerialAssignmentModal
                    open={podModalOpen}
                    onClose={() => setPodModalOpen(false)}
                    orderUuid={order.uuid}
                    order={order}
                    onSaved={(updated) => {
                        onFp1OrderRefresh?.(updated);
                    }}
                />
            )}
            <style>
                {`
                    @media print {
                        /* Hide/show elements based on print mode */
                        .print-only { display: block !important; }
                        .no-print { display: none !important; }
                        
                        /* Page setup */
                        @page {
                            margin: 12mm;
                            size: letter portrait;
                        }
                        
                        body {
                            margin: 0;
                            padding: 0;
                            font-family: Helvetica, Arial, sans-serif !important;
                            font-size: 9pt;
                            line-height: 1.3;
                        }
                        
                        /* Remove all Material UI shadows */
                        .MuiPaper-root { 
                            box-shadow: none !important;
                            border: none !important;
                        }
                        
                        /* Logo */
                        .print-logo {
                            max-width: 120px !important;
                            height: auto !important;
                            margin: 0 auto 6px !important;
                            display: block !important;
                        }
                        
                        /* Header styling */
                        .print-header {
                            text-align: center;
                            margin-bottom: 8px !important;
                            padding-bottom: 4px !important;
                            border-bottom: 1px solid #B4E12C !important;
                        }
                        
                        .print-header h4 {
                            font-size: 14pt !important;
                            color: #000 !important;
                            font-weight: bold !important;
                            letter-spacing: 1px !important;
                            margin: 5px 0 8px 0 !important;
                        }
                        
                        .print-header .order-info {
                            font-size: 9pt !important;
                            color: #000 !important;
                            margin: 3px 0 !important;
                        }
                        
                        .print-header .transaction-id {
                            font-size: 8pt !important;
                            color: #666 !important;
                            margin-top: 3px !important;
                        }
                        
                        /* Three column info table */
                        .info-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 6px 0;
                        }

                        .info-table th {
                            background: #B4E12C !important;
                            padding: 5px 8px !important;
                            font-size: 9pt !important;
                            font-weight: bold !important;
                            text-transform: uppercase !important;
                            text-align: left !important;
                            border: 1px solid #B4E12C !important;
                        }
                        
                        .info-table td {
                            padding: 5px 8px !important;
                            font-size: 9pt !important;
                            border: 1px solid #B4E12C !important;
                            vertical-align: top !important;
                        }
                        
                        .info-table td.info-cell {
                            line-height: 1.5 !important;
                        }
                        
                        .info-table td div {
                            margin: 2px 0 !important;
                        }
                        
                        .info-bold {
                            font-weight: bold !important;
                        }
                        
                        /* Checklist box */
                        .checklist-box {
                            border: 1px solid #B4E12C;
                            padding: 0;
                            margin: 6px 0;
                        }

                        .checklist-header-bar {
                            background: #B4E12C !important;
                            padding: 4px 10px !important;
                            font-size: 10pt !important;
                            font-weight: bold !important;
                            text-transform: uppercase !important;
                        }
                        
                        .checklist-content {
                            padding: 6px 10px;
                        }
                        
                        .checklist-item-print {
                            font-size: 9pt !important;
                            margin: 5px 0 !important;
                        }
                        
                        .checklist-checkbox {
                            display: inline-block;
                            width: 10px;
                            height: 10px;
                            border: 1px solid #B4E12C;
                            margin-right: 6px;
                            vertical-align: middle;
                            background: #fff;
                        }
                        
                        /* Line Items table */
                        .print-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 6px 0;
                        }

                        .print-table-header-bar {
                            background: #B4E12C !important;
                            padding: 4px 10px !important;
                            font-size: 10pt !important;
                            font-weight: bold !important;
                            text-transform: uppercase !important;
                            text-align: left !important;
                        }

                        .print-table th {
                            background: #B4E12C !important;
                            border: 1px solid #B4E12C !important;
                            padding: 4px 6px !important;
                            font-size: 9pt !important;
                            font-weight: bold !important;
                        }

                        .print-table th.text-center {
                            text-align: center !important;
                        }

                        .print-table th.text-right {
                            text-align: right !important;
                        }

                        .print-table td {
                            border: 1px solid #B4E12C !important;
                            padding: 4px 6px !important;
                            font-size: 9pt !important;
                            background: #fff !important;
                        }
                        
                        .print-table td.text-center {
                            text-align: center !important;
                        }
                        
                        .print-table td.text-right {
                            text-align: right !important;
                        }
                        
                        /* Order Summary box */
                        .summary-box-wrapper {
                            margin: 6px 0 8px 0;
                        }

                        .summary-header-bar {
                            background: #B4E12C !important;
                            padding: 4px 10px !important;
                            font-size: 10pt !important;
                            font-weight: bold !important;
                            text-transform: uppercase !important;
                            text-align: right !important;
                        }
                        
                        .summary-box {
                            float: right;
                            width: 300px;
                            padding: 5px 10px;
                        }

                        .summary-line {
                            display: flex;
                            justify-content: space-between;
                            padding: 2px 0;
                            font-size: 9pt;
                        }
                        
                        .summary-total {
                            border-top: 2px solid #B4E12C;
                            padding-top: 6px !important;
                            margin-top: 4px;
                            font-weight: bold !important;
                            color: #B4E12C !important;
                            font-size: 11pt !important;
                        }
                        
                        /* Order notes box */
                        .order-notes-box {
                            border: 1px solid #B4E12C;
                            padding: 0;
                            margin-top: 8px;
                            clear: both;
                        }

                        .order-notes-header-bar {
                            background: #B4E12C !important;
                            padding: 4px 10px !important;
                            font-size: 10pt !important;
                            font-weight: bold !important;
                            text-align: left !important;
                            text-transform: uppercase !important;
                        }

                        .order-notes-content {
                            padding: 8px 10px;
                        }

                        .order-notes-box p {
                            font-size: 9pt !important;
                            line-height: 1.3 !important;
                            margin: 0 !important;
                            white-space: pre-wrap !important;
                        }

                        /* Lease terms box */
                        .lease-box {
                            border: 1px solid #ff0000;
                            padding: 0;
                            margin-top: 8px;
                            clear: both;
                        }

                        .lease-header-bar {
                            background: #B4E12C !important;
                            padding: 5px 10px !important;
                            font-size: 10pt !important;
                            font-weight: bold !important;
                            text-align: center !important;
                            text-transform: uppercase !important;
                        }

                        .lease-content {
                            padding: 8px 10px;
                        }

                        .lease-box p {
                            font-size: 8pt !important;
                            line-height: 1.3 !important;
                            margin-bottom: 4px !important;
                            text-align: justify !important;
                        }

                        .lease-box strong {
                            font-weight: bold !important;
                            text-transform: uppercase !important;
                        }

                        .lease-box .acknowledgment {
                            text-align: center !important;
                            margin-top: 6px !important;
                        }

                        .print-page {
                            display: block;
                        }
                    }
                    
                    /* Default: hide print-only elements on screen */
                    .print-only { display: none; }
                `}
            </style>

            <PrintableDialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '90vh',
                        m: 2,
                    },
                }}
            >
                {/* Modal Header */}
                <DialogTitle className="no-print" sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h5" component="span">
                                Edit Order
                            </Typography>
                            <Chip
                                label={orderStatus}
                                color={getOrderStatusColor(orderStatus)}
                                size="small"
                                sx={{ ml: 2 }}
                            />
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                    >
                        Created: {order.created_at.toLocaleString()}
                    </Typography>
                </DialogTitle>

                <Divider className="no-print" />

                <DialogContent sx={{ pt: 3, position: 'relative' }}>
                    <Box className="print-page">
                        {/* Print Logo */}
                        <Box className="print-only" sx={{ textAlign: 'center', mb: 2 }}>
                            <img src={FiyrPodLogo} alt="FiyrPod" className="print-logo" />
                        </Box>

                        {/* Print Header */}
                        <Box className="print-only print-header">
                            <Typography variant="h4" component="h4">
                                ORDER RECEIPT
                            </Typography>
                            <Typography className="order-info">
                                <strong>
                                    Order #
                                    {order.uuid.replace(/-/g, '').substring(12, 20).toUpperCase()}
                                </strong>{' '}
                                | Ordered on:{' '}
                                {new Date(order.created_at).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </Typography>
                            <Typography className="transaction-id">
                                Transaction ID: {order.stripe_sale_id}
                            </Typography>
                        </Box>

                        {/* Three Column Info Table - Print Only */}
                        <Box className="print-only">
                            <table className="info-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '33.33%' }}>Seller Information</th>
                                        <th style={{ width: '33.33%' }}>Buyer Information</th>
                                        <th style={{ width: '33.33%' }}>Ship To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="info-cell">
                                            <div className="info-bold">{order.seller_org_name}</div>
                                            {sellerOrg && sellerOrg.creator && (
                                                <>
                                                    <div>
                                                        {sellerOrg.creator.first_name}{' '}
                                                        {sellerOrg.creator.last_name}
                                                    </div>
                                                    <div>{sellerOrg.creator.email}</div>
                                                    <div>{sellerOrg.creator.phone_national}</div>
                                                </>
                                            )}
                                        </td>
                                        <td className="info-cell">
                                            <div className="info-bold">
                                                {order.buyer_org_name ||
                                                    `${order.buyer_athlete?.first_name} ${order.buyer_athlete?.last_name}`}
                                            </div>
                                            {buyerOrg && buyerOrg.creator ? (
                                                <>
                                                    <div>
                                                        {buyerOrg.creator.first_name}{' '}
                                                        {buyerOrg.creator.last_name}
                                                    </div>
                                                    <div>{buyerOrg.creator.email}</div>
                                                    <div>{buyerOrg.creator.phone_national}</div>
                                                </>
                                            ) : (
                                                order.buyer_athlete && (
                                                    <>
                                                        <div>{order.buyer_athlete.email}</div>
                                                        {order.buyer_athlete.phone && (
                                                            <div>{order.buyer_athlete.phone}</div>
                                                        )}
                                                    </>
                                                )
                                            )}
                                        </td>
                                        <td className="info-cell">
                                            <div className="info-bold">
                                                {order.buyer_org_name ||
                                                    `${order.buyer_athlete?.first_name} ${order.buyer_athlete?.last_name}`}
                                            </div>
                                            <div>{order.shipping_address_1}</div>
                                            {order.shipping_address_2 && (
                                                <div>{order.shipping_address_2}</div>
                                            )}
                                            <div>
                                                {order.shipping_city}, {order.shipping_state}{' '}
                                                {order.shipping_zip}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>

                        {/* Screen Version - Seller, Buyer, Ship To */}
                        <PageBreakAvoid>
                            <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Seller Information
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.seller_org_name}
                                        </Typography>
                                        {sellerOrg && (
                                            <>
                                                {sellerOrg.subscription_tier && (
                                                    <Chip
                                                        label={sellerOrg.subscription_tier.name}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ mb: 1, mt: 0.5 }}
                                                    />
                                                )}
                                                {sellerOrg.creator && (
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            fontWeight="medium"
                                                        >
                                                            {sellerOrg.creator.first_name}{' '}
                                                            {sellerOrg.creator.last_name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {sellerOrg.creator.email}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {sellerOrg.creator.phone_national}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Buyer Information
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.buyer_org_name ||
                                                `${order.buyer_athlete?.first_name} ${order.buyer_athlete?.last_name}`}
                                        </Typography>
                                        {buyerOrg && buyerOrg.subscription_tier && (
                                            <Chip
                                                label={buyerOrg.subscription_tier.name}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ mb: 1, mt: 0.5 }}
                                            />
                                        )}
                                        {buyerOrg && buyerOrg.creator ? (
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    fontWeight="medium"
                                                >
                                                    {buyerOrg.creator.first_name}{' '}
                                                    {buyerOrg.creator.last_name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                >
                                                    {buyerOrg.creator.email}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                >
                                                    {buyerOrg.creator.phone_national}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            order.buyer_athlete && (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                    >
                                                        {order.buyer_athlete.email}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="text.secondary"
                                                    >
                                                        {order.buyer_athlete.phone}
                                                    </Typography>
                                                </Box>
                                            )
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <LocalShippingIcon color="primary" fontSize="small" />
                                            <Typography variant="h6">Ship To:</Typography>
                                        </Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.buyer_org_name ||
                                                `${order.buyer_athlete?.first_name} ${order.buyer_athlete?.last_name}`}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                            {order.shipping_address_1}
                                        </Typography>
                                        {order.shipping_address_2 && (
                                            <Typography variant="caption" display="block">
                                                {order.shipping_address_2}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" display="block">
                                            {order.shipping_city}, {order.shipping_state}{' '}
                                            {order.shipping_zip}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            {fp1HardwareOrdersTab && fp1PodRequired > 0 && (
                                                <Box
                                                    sx={{
                                                        mb: 1,
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {order.pod_serials_assigned ? (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="success"
                                                                disabled
                                                            >
                                                                Pod Serials Assigned ✓
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                onClick={() =>
                                                                    setPodModalOpen(true)
                                                                }
                                                            >
                                                                Edit Assignments
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => setPodModalOpen(true)}
                                                        >
                                                            Assign Pod Serial Numbers
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                            <Tooltip
                                                title={
                                                    trackingLocked
                                                        ? 'Assign pod serial numbers first'
                                                        : ''
                                                }
                                            >
                                                <span style={{ display: 'block' }}>
                                                    <TextField
                                                        label="Outbound Tracking Number"
                                                        value={trackingNumber}
                                                        onChange={(e) =>
                                                            setTrackingNumber(e.target.value)
                                                        }
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={trackingLocked}
                                                    />
                                                </span>
                                            </Tooltip>
                                            {order.order_type === 'replacement' && (
                                                <TextField
                                                    label="Return Tracking Number (Inbound)"
                                                    value={returnTrackingNumber}
                                                    onChange={(e) =>
                                                        setReturnTrackingNumber(e.target.value)
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mt: 1.5 }}
                                                    placeholder="Enter when athlete ships back defective pod"
                                                    helperText={
                                                        returnTrackingNumber
                                                            ? undefined
                                                            : 'Recommended for replacement orders'
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </PageBreakAvoid>

                        {/* Pre-Ship Checklist - Print Only */}
                        {order.order_status !== 'backfilled' && order.order_type !== 'digital' && (
                            <Box className="print-only checklist-box">
                                <div className="checklist-header-bar">Pre-Ship Checklist</div>
                                <div className="checklist-content">
                                    {getChecklistItems().map((item, index) => (
                                        <div key={index} className="checklist-item-print">
                                            <span className="checklist-checkbox"></span>
                                            {item.text}
                                            {item.requiresInitial && ' (Initial: __________)'}
                                        </div>
                                    ))}
                                </div>
                            </Box>
                        )}

                        {/* Line Items Table - Print Only */}
                        <Box className="print-only">
                            <div className="print-table-header-bar">Line Items</div>
                            <table className="print-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '50%' }}>Description</th>
                                        <th className="text-center" style={{ width: '10%' }}>
                                            Qty
                                        </th>
                                        <th className="text-right" style={{ width: '17.5%' }}>
                                            Unit Price
                                        </th>
                                        <th className="text-right" style={{ width: '17.5%' }}>
                                            Subtotal
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.line_items.map((item, index) => (
                                        <tr key={item.uuid}>
                                            <td>{index + 1}</td>
                                            <td>{item.description}</td>
                                            <td className="text-center">{item.quantity}</td>
                                            <td className="text-right">
                                                {formatCurrency(
                                                    item.unit_amount,
                                                    item.stripe_price_id
                                                        .toLowerCase()
                                                        .includes('coupon'),
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {formatCurrency(
                                                    item.amount_subtotal,
                                                    item.stripe_price_id
                                                        .toLowerCase()
                                                        .includes('coupon'),
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>

                        {/* Order Summary - Print Only */}
                        <Box className="print-only summary-box-wrapper">
                            <div className="summary-header-bar">Order Summary</div>
                            <Box className="summary-box">
                                <Box className="summary-line">
                                    <span>Subtotal:</span>
                                    <span>{formatCurrency(order.line_item_total)}</span>
                                </Box>
                                <Box className="summary-line">
                                    <span>Tax:</span>
                                    <span>{formatCurrency(order.tax)}</span>
                                </Box>
                                <Box className="summary-line">
                                    <span>Shipping:</span>
                                    <span>{formatCurrency(order.shipping)}</span>
                                </Box>
                                <Box className="summary-line summary-total">
                                    <span>
                                        <strong>TOTAL:</strong>
                                    </span>
                                    <span>
                                        <strong>{formatCurrency(order.total)}</strong>
                                    </span>
                                </Box>
                            </Box>
                        </Box>

                        {/* Screen Version - Line Items Table */}
                        <PageBreakAvoid className="no-print">
                            <Paper elevation={2} sx={{ mb: 3 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="h6">Line Items</Typography>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width="40">#</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="center" width="60">
                                                    Qty
                                                </TableCell>
                                                <TableCell align="right" width="100">
                                                    Unit Price
                                                </TableCell>
                                                <TableCell align="right" width="100">
                                                    Subtotal
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {order.line_items.map((item, index) => (
                                                <TableRow key={item.uuid}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                        >
                                                            {item.description}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {item.quantity}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">
                                                            {formatCurrency(
                                                                item.unit_amount,
                                                                item.stripe_price_id
                                                                    .toLowerCase()
                                                                    .includes('coupon'),
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                        >
                                                            {formatCurrency(
                                                                item.amount_subtotal,
                                                                item.stripe_price_id
                                                                    .toLowerCase()
                                                                    .includes('coupon'),
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Screen-only summary card */}
                            <Box sx={{ maxWidth: '400px', ml: 'auto' }}>
                                <SummaryCard elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Order Summary
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Transaction ID:
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            fontFamily="monospace"
                                            sx={{ wordBreak: 'break-all', mb: 1 }}
                                        >
                                            {order.stripe_sale_id}
                                        </Typography>

                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            <a
                                                href={`https://dashboard.stripe.com/payments/${order.stripe_sale_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                View in Stripe →
                                            </a>
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={0.5}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Subtotal:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(order.line_item_total)}
                                                </Typography>
                                            </Box>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={0.5}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Tax:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(order.tax)}
                                                </Typography>
                                            </Box>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={0.5}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Shipping:
                                                </Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(order.shipping)}
                                                </Typography>
                                            </Box>
                                            <Divider sx={{ my: 1 }} />
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Total:
                                                </Typography>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                    color="primary"
                                                >
                                                    {formatCurrency(order.total)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </SummaryCard>
                            </Box>
                        </PageBreakAvoid>

                        {/* Order Notes - Print Only */}
                        {orderNotes && orderNotes.trim() !== '' && (
                            <Box className="print-only order-notes-box">
                                <div className="order-notes-header-bar">Order Notes</div>
                                <div className="order-notes-content">
                                    <p>{orderNotes}</p>
                                </div>
                            </Box>
                        )}

                        {/* Lease Terms - Print Only */}
                        {order.order_status !== 'backfilled' && order.order_type !== 'digital' && (
                            <Box className="print-only lease-box">
                                <div className="lease-header-bar">
                                    ■■ Important: Leased Equipment & Subscription Terms
                                </div>
                                <div className="lease-content">
                                    <p>
                                        This order includes leased GPS equipment with a recurring
                                        subscription that continues until properly cancelled through
                                        the return process outlined below.
                                    </p>

                                    <p>
                                        <strong>Subscription Billing:</strong> Your subscription
                                        remains ACTIVE and billing continues as long as you possess
                                        the FiyrPod unit(s). Billing will NOT stop if you: (1) stop
                                        using the product, (2) delete the app, (3) give the pod to a
                                        coach, school, team, or third party, or (4) request
                                        cancellation without returning the unit. Only physical
                                        return of the pod to FiyrPod will cancel your subscription.
                                    </p>

                                    <p>
                                        <strong>Cancellation Process:</strong> To cancel your
                                        subscription and stop future billing:
                                    </p>
                                    <p style={{ paddingLeft: '12px' }}>
                                        1. Ship the pod to:{' '}
                                        <strong>
                                            Fiyrpod Returns &amp; Warranty, 240 Riverview Parkway,
                                            Santee, CA 92071
                                        </strong>
                                        <br />
                                        2. Billing continues until FiyrPod receives and inspects the
                                        returned pod.
                                        <br />
                                        3. Returns are processed within 2–3 business days after
                                        receipt.
                                        <br />
                                        4.{' '}
                                        <em>
                                            Third-party possession does NOT constitute a return.
                                        </em>{' '}
                                        Only FiyrPod can process cancellations upon receipt of the
                                        unit at our facility.
                                    </p>

                                    <p>
                                        <strong>Returns:</strong> Customers are responsible for
                                        return shipping costs unless the item is defective or
                                        incorrect. Products must be returned in original condition
                                        with all accessories and packaging included. If possible,
                                        please include a copy of your original order receipt to help
                                        expedite return processing. For return assistance, contact{' '}
                                        <strong>support@fiyrpod.com</strong> or your business
                                        sponsor listed above.
                                    </p>

                                    <p className="acknowledgment">
                                        <strong>
                                            By completing this purchase, you acknowledge and agree
                                            to these terms.
                                        </strong>
                                    </p>
                                </div>
                            </Box>
                        )}
                    </Box>

                    {/* Order Management - Screen Only */}
                    <Paper elevation={2} sx={{ p: 3 }} className="no-print">
                        <Typography variant="h6" gutterBottom>
                            Order Management
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Order Notes"
                                    value={orderNotes}
                                    onChange={(e) => setOrderNotes(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Order Status</InputLabel>
                                    <Select
                                        value={orderStatus}
                                        label="Order Status"
                                        onChange={(e) => setOrderStatus(e.target.value as string)}
                                    >
                                        {orderStatuses.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>
                </DialogContent>

                <Divider className="no-print" />

                <DialogActions className="no-print" sx={{ p: 2 }}>
                    <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined">
                        Print Order
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </PrintableDialog>

            {/* Update Result Dialog */}
            <Dialog
                open={updateOrderModalOpen}
                onClose={() => setUpdateOrderModalOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Update Status</DialogTitle>
                <Divider />
                <DialogContent>
                    <Typography sx={{ pt: 2 }}>{updateOrderResult}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateOrderModalOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Return Tracking Warning Dialog */}
            <Dialog
                open={returnTrackingWarningOpen}
                onClose={() => setReturnTrackingWarningOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="warning" />
                    Missing Return Tracking Number
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Typography sx={{ pt: 1 }}>
                        This is a <strong>replacement order</strong> with an outbound tracking
                        number, but no return tracking number has been entered.
                    </Typography>
                    <Typography sx={{ mt: 1.5 }} color="text.secondary" variant="body2">
                        Adding a return tracking number lets you monitor whether the athlete has
                        shipped back the defective pod. You can add it now or return to the order
                        later.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ gap: 1, p: 2 }}>
                    <Button onClick={() => setReturnTrackingWarningOpen(false)} variant="outlined">
                        Go Back &amp; Add Return Tracking
                    </Button>
                    <Button
                        onClick={async () => {
                            setReturnTrackingWarningOpen(false);
                            if (pendingSaveParams) {
                                await executeSave(pendingSaveParams);
                            }
                        }}
                        variant="contained"
                        color="warning"
                    >
                        Continue Without Return Tracking
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OrderDialog;
